import type { AxiosError } from 'axios';
import type { ApiErrorResponse } from '@personnel/application/errors/apiErrorTypes';

/**
 * Type guard to check if error is an ApiErrorResponse
 *
 * @param error - Unknown error object
 * @returns True if error matches ApiErrorResponse structure
 *
 * @example
 * if (isApiErrorResponse(error)) {
 *   // error is now typed as ApiErrorResponse
 *   console.log(error.errors[0].code);
 * }
 */
export const isApiErrorResponse = (error: unknown): error is ApiErrorResponse => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as ApiErrorResponse).errors) &&
    (error as ApiErrorResponse).errors.length > 0 &&
    typeof (error as ApiErrorResponse).errors[0].code === 'string' &&
    typeof (error as ApiErrorResponse).errors[0].message === 'string'
  );
};

/**
 * Type guard to check if error is an AxiosError
 *
 * @param error - Unknown error object
 * @returns True if error is an AxiosError
 *
 * @example
 * if (isAxiosError(error)) {
 *   console.log(error.response?.status);
 * }
 */
export const isAxiosError = (error: unknown): error is AxiosError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
};

/**
 * Type guard to check if AxiosError contains ApiErrorResponse
 *
 * Verifies that:
 * 1. error.response exists
 * 2. error.response.data exists
 * 3. error.response.data matches ApiErrorResponse structure
 *
 * @param error - Axios error object
 * @returns True if response data is ApiErrorResponse
 *
 * @example
 * if (isAxiosError(error) && hasApiErrorResponse(error)) {
 *   const apiError = error.response.data; // Typed as ApiErrorResponse
 * }
 */
export const hasApiErrorResponse = (error: AxiosError): error is AxiosError<ApiErrorResponse> => {
  return (
    error.response !== undefined &&
    error.response.data !== undefined &&
    error.response.data !== null &&
    isApiErrorResponse(error.response.data)
  );
};
